import React, { useState, useEffect, useCallback, useRef } from "react";
import QRCode from "react-qr-code";
import { Box, Typography, Tooltip, CircularProgress, IconButton } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import axios from "axios";

/**
 * QR for patient phone → /upload_handwritten_prescription (same pattern as voice QR).
 * Token API: POST generate-upload-token/
 */
const QrCodeHandwrittenPrescriptionUpload = ({
  patientId,
  orgCode,
  appMode,
  facilityName,
  authToken,
  backendUrl,
  active,
}) => {
  const [token, setToken] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const timerRef = useRef(null);
  const apiBase = backendUrl || process.env.REACT_APP_BACKEND_ROUTE_URL || "";

  const getAuthHeaders = useCallback(() => {
    const tokenHdr = authToken || sessionStorage.getItem("accessToken") || "";
    const mode =
      sessionStorage.getItem("APP_MODE") ||
      sessionStorage.getItem("appMode") ||
      "";
    const org =
      sessionStorage.getItem("ORG_CODE") ||
      sessionStorage.getItem("orgCode") ||
      "";
    const username =
      sessionStorage.getItem("userName") ||
      sessionStorage.getItem("username") ||
      "";
    const accessKey =
      sessionStorage.getItem("USER_ACCESS_KEY") ||
      sessionStorage.getItem("accessKey") ||
      "";
    return {
      Authorization: `Bearer ${tokenHdr}`,
      "Content-Type": "application/json",
      "App-Mode": mode,
      "Org-Code": org,
      Username: username,
      "User-Access-Key": accessKey,
    };
  }, [authToken]);

  const fetchToken = useCallback(async () => {
    if (!patientId || !orgCode || !active) return;

    setLoading(true);
    setError("");
    try {
      const response = await axios.post(
        `${apiBase}generate-upload-token/`,
        {
          patient_id: patientId,
          org_code: orgCode,
          app_mode: appMode || "",
          facility_name: facilityName || "",
        },
        { headers: getAuthHeaders() }
      );

      if (response.data?.status === "Success" && response.data?.token) {
        setToken(response.data.token);
        clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
          fetchToken();
        }, 9.5 * 60 * 1000);
      } else {
        setError("Could not generate QR");
        setToken("");
      }
    } catch (e) {
      console.error("Handwritten prescription QR token error:", e);
      setError("Could not generate QR");
      setToken("");
    } finally {
      setLoading(false);
    }
  }, [
    patientId,
    orgCode,
    appMode,
    facilityName,
    active,
    apiBase,
    getAuthHeaders,
  ]);

  useEffect(() => {
    if (active && patientId && orgCode) {
      fetchToken();
    } else {
      setToken("");
      setError("");
    }
    return () => {
      clearTimeout(timerRef.current);
    };
  }, [active, patientId, orgCode, fetchToken]);

  const baseUrl = window.location.origin;
  // Keep URL short for reliable QR scanning:
  // patient_id/org_code/app_mode are already inside the encrypted token.
  const uploadUrl = token
    ? `${baseUrl}/upload_handwritten_prescription?token=${encodeURIComponent(
        token
      )}`
    : "";

  if (!patientId || !orgCode) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: 1.5,
          border: "1px solid #e0e0e0",
          borderRadius: 2,
          bgcolor: "#fafafa",
          minWidth: 140,
          minHeight: 110,
        }}
      >
        <Typography variant="caption" sx={{ color: "#999", textAlign: "center", px: 1 }}>
          Open with a patient selected to show upload QR
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "8px",
        border: "1px solid #e0e0e0",
        borderRadius: "8px",
        backgroundColor: "#fff",
        boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
        maxWidth: 156,
        margin: { xs: "12px auto 0", sm: "0 0 0 auto" },
        minHeight: 118,
        flexShrink: 0,
      }}
    >
      {loading ? (
        <CircularProgress size={24} sx={{ my: 2 }} />
      ) : error ? (
        <Box sx={{ textAlign: "center", py: 1 }}>
          <Typography variant="caption" sx={{ color: "#d32f2f", display: "block", px: 0.5 }}>
            {error}
          </Typography>
          <IconButton size="small" onClick={fetchToken} color="primary" aria-label="retry QR">
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Box>
      ) : (
        <>
          <Tooltip title="Scan with your phone to upload handwritten prescription photos" arrow>
            <Box sx={{ bgcolor: "#fff", p: 0.5, borderRadius: "4px" }}>
              {uploadUrl ? (
                <QRCode
                  value={uploadUrl}
                  size={120}
                  style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                  viewBox="0 0 256 256"
                />
              ) : (
                <Box
                  sx={{
                    height: 88,
                    width: 88,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "#f5f5f5",
                  }}
                >
                  <Typography variant="caption" sx={{ fontSize: "0.55rem", textAlign: "center", px: 0.5 }}>
                    QR unavailable
                  </Typography>
                </Box>
              )}
            </Box>
          </Tooltip>
          <Typography
            variant="caption"
            sx={{
              mt: 0.5,
              fontWeight: 700,
              fontSize: "0.6rem",
              color: "#b45309",
              textAlign: "center",
              lineHeight: 1.2,
            }}
          >
            SCAN TO UPLOAD RX
          </Typography>
          <Typography variant="caption" sx={{ fontSize: "0.55rem", color: "#888", textAlign: "center" }}>
            Expires ~10 min
          </Typography>
        </>
      )}
    </Box>
  );
};

export default QrCodeHandwrittenPrescriptionUpload;
